import type { I18n } from 'vue-i18n';

/**
 * 套餐版本类型
 */
export enum EditionType {
    BASIC = 'basic',
    RD_PRO = 'rd_pro',
    ENTERPRISE = 'enterprise',
    API = 'api'
}

export interface PlgPlanSDKOptions {
    i18n?: I18n;
}

export interface PlgPlanSDKOpenOptions {
    withoutClose?: boolean;
    successUrl?: string;
    cancelUrl?: string;
    viewMoreUrl?: string;
}

export enum PACKAGE_FEATURES {
    AI_POWERED_QA = 'AI_POWERED_QA',
    TRIZ_SOLUTIONS = 'TRIZ_SOLUTIONS',
    PRE_RESEARCH_REPORTS = 'PRE_RESEARCH_REPORTS',
    AI_FMEA = 'AI_FMEA',
    AI_MONITORING_INSIGHTS = 'AI_MONITORING_INSIGHTS',
    PATENT_SUMMARY = 'PATENT_SUMMARY',
    COLLABORATION_TOOLS = 'COLLABORATION_TOOLS',
    INCOGNITO_MODE = 'INCOGNITO_MODE',
    PATENT_SEARCH = 'PATENT_SEARCH',
    AI_TECHNICAL_DISCLOSURE_ASSISTANT = 'AI_TECHNICAL_DISCLOSURE_ASSISTANT',
    AI_NOVELTY_SEARCH = 'AI_NOVELTY_SEARCH',
    FIRST_PRINCIPLE_SOLUTIONS = 'FIRST_PRINCIPLE_SOLUTIONS',
    MATERIAL_PROPERTIES_EXPLORATION = 'MATERIAL_PROPERTIES_EXPLORATION',
    SUBSTANCE_INQUIRY = 'SUBSTANCE_INQUIRY',
    METAL_COMPOSITION_SEARCH = 'METAL_COMPOSITION_SEARCH',
    STRUCTURE_SEARCH = 'STRUCTURE_SEARCH',
    MATERIAL_SYNTHESIS_EXPLORATION = 'MATERIAL_SYNTHESIS_EXPLORATION',
    PERFORMANCE_ANALYSIS = 'PERFORMANCE_ANALYSIS',
    COMPANY_SEARCH = 'COMPANY_SEARCH',
    MUTIPLE_SOURCES = 'MUTIPLE_SOURCES',
    COMPETITIVE_LANDSCAPE = 'COMPETITIVE_LANDSCAPE',
    LS_PHARMA_INSIGHTS_ADVISOR = 'LS_PHARMA_INSIGHTS_ADVISOR',
    LS_PHARMA_DOCUMENT_ANALYZER = 'LS_PHARMA_DOCUMENT_ANALYZER',
    LS_MARKUSH_CLAIM_DRAFTING = 'LS_MARKUSH_CLAIM_DRAFTING',
    LS_GENERIC_DRUG_ID_CHECKER = 'LS_GENERIC_DRUG_ID_CHECKER',
    LS_DRUGGABLE_ANTIBODY_FINDER = 'LS_DRUGGABLE_ANTIBODY_FINDER',
    LS_GENERICS_TO_INNOVATE_NAVIGATOR = 'LS_GENERICS_TO_INNOVATE_NAVIGATOR',

    IP_NOVELTY = 'IP_NOVELTY',
    IP_SPECIFICATION = 'IP_SPECIFICATION',
    IP_DESIGN_INFRINGEMENT = 'IP_DESIGN_INFRINGEMENT',
    IP_INFRINGEMENT = 'IP_INFRINGEMENT',
    IP_TRANSLATION = 'IP_TRANSLATION'
}

export enum PAID_STATUS {
    LOW_VERSION = 0, // 低版本
    PAID = 1, // 已付费
    UNPAID = 2, // 未付费（待升级）
    COMMING_SOON = 3 // 即将上线
}

export interface ICreatePaymentSessionRequest {
    /**
     * 价格 ID 集合:从Stripe获取
     */
    price_ids: string[];
    /**
     * 支付模式
     */
    mode?: 'subscription';
    /**
     * UI 模式
     * @default 'hosted'
     */
    ui_mode?: 'hosted' | 'custom' | 'embedded';
    /**
     * UI Mode=/custom/embedded时需要
     */
    return_url?: string;
    /**
     * UI Mode=hosted时支付成功跳转地址
     */
    success_url?: string;
    /**
     * UI Mode=hosted时取消支付跳转地址
     */
    cancel_url?: string;
    /**
     * 是否自动计算税费
     * @default false
     */
    automatic_tax?: boolean;
    /**
     * 试用天数 (最大 15 天)
     * @default 7
     */
    trial_days?: number;
}

export interface ICreatePaymentSessionResponse {
    /**
     * Stripe 客户 ID
     */
    customer_id: string;
    /**
     * 结账页面 URL
     */
    url: string;
    /**
     * 客户端密钥
     */
    clint_secret: string;
}

export interface ICancelSubscriptionRequest {
    /**
     * 订阅 ID
     */
    subscription_id: string;
    /**
     * 取消原因
     * customer_service | low_quality | missing_features | other | switched_service | too_complex | too_expensive | unused
     */
    reason?: string;
    /**
     * 备注信息
     */
    remark?: string;
}

export interface IResumeSubscriptionRequest {
    /**
     * 订阅 ID
     */
    subscription_id: string;
}

export interface IGetInvoicesRequest {
    /**
     * 订阅 ID
     */
    subscription_id: string;
    /**
     * 操作类型
     */
    action_type?: string;
    /**
     * 返回 URL
     */
    return_url?: string;
}

export interface ISubscriptionItem {
    amount: number;
    concurrency: string;
    current_period_end: string;
    current_period_start: string;
    interval_type: string;
    price_id: string;
    product_name: string;
}

export enum ESubscriptionStatus {
    'trialing' = 'trialing',
    'active' = 'active',
    'canceled' = 'canceled',
    'ended' = 'ended',
    'past_due' = 'past_due',
    'unpaid' = 'unpaid'
}

export interface ISubscriptionListDTO {
    /**
     * 订阅 ID
     */
    subscription_id: string;
    /**
     * 订阅状态
     */
    subscription_status: ESubscriptionStatus;
    /**
     * 创建时间
     */
    created_at: string;
    /**
     * 当前周期开始时间
     */
    current_period_start: string;
    /**
     * 当前周期结束时间
     */
    current_period_end: string;
    /**
     * 取消时间，如果有的话，说明在订阅期内取消订阅了
     */
    cancel_at?: string;
    /**
     * 订阅项目列表
     */
    items: ISubscriptionItem[]
}

export interface PricingPlan {
    id: string;
    editionType?: EditionType;
    price?: number | string;
    originalPrice?: number | null;
    yearSaveAmount?: number;
    priceUnit: string;
    priceNote?: string;
    highlighted: boolean;
    buttonStyle: 'primary' | 'outline';
    badge: string | null;
    disabled: boolean;
    isRecommended: boolean;
    isCurrent: boolean;
    features: {title: string, items: string[]}[];
}

export interface PlanConfig {
    /**
     * 每小时恢复积分
     */
    credits: number;
    /**
     * 月付原价
     */
    originalPrice: number;
    /**
     * 月付促销价
     */
    salePrice: number;
    /**
     * 年付原价
     */
    yearlyOriginalPrice?: number;
    /**
     * 年付促销价
     */
    yearlySalePrice?: number;
    /**
     * emergency pack 原价
     */
    emergencyPackOriginalPrice?: number;
    /**
     * emergency pack 销售价
     */
    emergencyPackSalePrice?: number;
    /**
     * emergency pack 折扣率
     */
    emergencyPackSaleDiscount?: number;
    /**
     * project pack 原价
     */
    projectPackOriginalPrice?: number;
    /**
     * project pack 销售价
     */
    projectPackSalePrice?: number;
    /**
     * project pack 折扣率
     */
    projectPackSaleDiscount?: number;
}
